/**
 * `@tento/lona/sheets/mutation` — the mutation substrate: local state as
 * `base ⊕ overlay`.
 *
 * Audience: a transport that implements the backend seam (`MutationBackend`,
 * with its `PendingMutation` / `MutationAck` / `ConfirmResult` contract), and
 * the SDK layer that drives it (`Overlay`, `MutationController`, the gesture
 * `MutationTransaction`, the snapshot `diffSnapshots` projection, and the
 * `event.v1` binding). Application code does not import it: an app mutates
 * through `@tento/lona`'s clients, which own an overlay per row.
 *
 * No new logic lives here — every name is a re-export of its module.
 *
 * @module
 */

// ─── Backend seam ────────────────────────────────────────────────
export type {
  CanonicalChange,
  CellSnapshot,
  MutationBackend,
} from "./mutation-backend.ts";
export type {
  ConfirmResult,
  MutationAck,
  PendingMutation,
  ToConfirmResult,
} from "./pending-mutation.ts";
export { MutationClientId } from "../ids/ids.ts";

// ─── Overlay + controller ────────────────────────────────────────
export { Overlay } from "./overlay.ts";
export type { Reduce } from "./overlay.ts";
export { MutationController } from "./mutation-controller.ts";

// ─── Gesture transactions ────────────────────────────────────────
export { GestureRollbackError, MutationTransaction } from "./mutation-transaction.ts";
export type { GestureRun, MutationDriver, TxStep } from "./mutation-transaction.ts";

// ─── Snapshot projection ─────────────────────────────────────────
export { diffSnapshots } from "./snapshot-diff.ts";
export type { ProjectionWrite } from "./snapshot-diff.ts";

// ─── The `event.v1` binding ──────────────────────────────────────
export {
  eventConfirmResult,
  eventReduce,
} from "./event-mutation-ops.ts";
export type { EventMutationOp } from "./event-mutation-ops.ts";

// The types the published signatures name (tools/lint/published-type-refs).
export type { RollbackRecord } from "../tx/tx-log.ts";
